/**
 * Darstellung: System · Hell · Dunkel.
 *
 * The pilot's choice is written on `<html data-theme>` and remembered in
 * localStorage. Its counterpart is the four-line script in `index.html`, which
 * applies the same key before the first paint so a light-theme user never sees
 * a dark flash; the key and the two values must stay in step with it.
 *
 * `system` writes no attribute at all — `tokens.css` declares light under
 * `@media (prefers-color-scheme: light) :root:not([data-theme='dark'])`, so the
 * absence of the attribute IS "follow the system", and the OS can change its
 * mind at sunset without this module hearing about it.
 */
import { useCallback, useEffect, useState } from 'react';

export type Theme = 'system' | 'light' | 'dark';

export const THEME_KEY = 'aether.theme';
export const THEMES: readonly Theme[] = ['system', 'light', 'dark'];

export function readTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* private mode: the system preference decides */
  }
  return 'system';
}

export function applyTheme(theme: Theme): void {
  const el = document.documentElement;
  if (theme === 'system') el.removeAttribute('data-theme');
  else el.setAttribute('data-theme', theme);
}

export function writeTheme(theme: Theme): void {
  applyTheme(theme);
  try {
    if (theme === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* the choice simply does not survive the tab */
  }
}

/**
 * What the page is actually painted in right now — the pilot's choice when they
 * made one, the OS's answer when they left it on System. Anything that has to
 * MATCH the theme rather than declare it (the map's basemap is the only such
 * thing today) asks this, never `readTheme`.
 */
export function resolvedTheme(): 'light' | 'dark' {
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'dark' || attr === 'light') return attr;
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/**
 * {@link resolvedTheme} as a subscription. Both halves matter: the media query
 * fires when the OS changes its mind at sunset, and the observer fires when
 * Einstellungen writes the attribute — neither alone is the whole answer.
 */
export function useResolvedTheme(): 'light' | 'dark' {
  const [value, setValue] = useState<'light' | 'dark'>(resolvedTheme);
  useEffect(() => {
    const update = () => setValue(resolvedTheme());
    const mq = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
    mq?.addEventListener('change', update);
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    update();
    return () => {
      mq?.removeEventListener('change', update);
      obs.disconnect();
    };
  }, []);
  return value;
}

/** The setting, as a control reads it. */
export function useTheme(): { theme: Theme; setTheme(t: Theme): void } {
  const [theme, setState] = useState<Theme>(readTheme);
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  const setTheme = useCallback((t: Theme) => {
    writeTheme(t);
    setState(t);
  }, []);
  return { theme, setTheme };
}
